/**
 * The arithmetic behind Brand Setup's three geometry cards (spec §2.2).
 *
 * Each of these screens asks ONE plain-language question — Small/Medium/Large,
 * Middle/Lower third, which corner — and has to turn the answer into the exact
 * numbers the render reads. That translation lives here, pure, for two reasons:
 *
 *  - The same numbers are needed in both directions. The size cards must map a
 *    STUDIED size back onto a card (`nearestSizePreset`) and a dragged speaker
 *    card back onto a corner (`cardPctToCorner`), or a prefilled screen would
 *    show nothing selected.
 *  - They are constants, not heuristics. The lower-third distance is derived
 *    from the caption size so the two can never drift apart, and the founding
 *    pair (165 → 240) is the calibration point: LTR Medium + Lower third must
 *    reproduce the distance the classic wizard ships.
 *
 * No DOM, no state — `geometry.test.ts` pins every number in node.
 */
import type { EditPatch } from "../../../../src/lib/clipEditState.ts";
import { LAYOUT_DEFAULTS } from "./payload";

/** Which script the caption is set in. Matches `LanguagePreset["direction"]`. */
export type Direction = "ltr" | "rtl";

export type SizeName = "small" | "medium" | "large";

/**
 * The three caption sizes each direction offers, in canvas px on a 2160-wide
 * frame. Medium is the direction's OWN default — LTR 165 is
 * `LATIN_CAPTION_DEFAULTS.fontSizePx`, RTL 115 is the founding
 * `capFontSizePx` — so landing on Medium and moving on writes no key at all
 * (spec §2.3: "a preselected card dispatches nothing on mount").
 */
export const SIZE_PRESETS = {
  ltr: { small: 130, medium: 165, large: 200 },
  rtl: { small: 100, medium: 115, large: 140 },
} as const;

const SIZE_NAMES: readonly SizeName[] = ["small", "medium", "large"];

/**
 * How far off the bottom a "Lower third" caption sits, for a given caption
 * size — `round(1.45 × size / 10) × 10`.
 *
 * Derived rather than fixed so the gap under the text stays proportional at
 * every size: a 200 px caption pinned at the 165 px distance would sit on the
 * frame edge. The multiplier is calibrated on the founding pair (165 → 240),
 * and the round-to-ten keeps the number one a human can read back in Adjust.
 */
export function lowerThirdBottomPx(fontSizePx: number): number {
  return Math.round((fontSizePx * 1.45) / 10) * 10;
}

export type Corner = "tl" | "tr" | "bl" | "br";

/**
 * The speaker card's default width as a % of the frame — the value the corner
 * picker assumes when it is not told otherwise. Only ever used to find the
 * card's LEFT edge for a right-hand corner.
 */
export const DEFAULT_CARD_WIDTH_PCT = 35;

/** The card's inset from the left edge — `LAYOUT_DEFAULTS.speakerCardXPct`. */
const INSET_PCT = 6.0185;
/** Top and bottom rows, as `LAYOUT_DEFAULTS.speakerCardYPct` and its mirror. */
const TOP_PCT = 8.333;
const BOTTOM_PCT = 78;

/**
 * Corner → the card's placement.
 *
 * ASYMMETRIC ON PURPOSE: `xPct` is the card's LEFT edge, not its anchor, so a
 * right-hand corner has to subtract the card's own width. Reading the width in
 * means an overridden card width still lands flush — the corner is recomputed
 * every time and NEVER stored as a name, so the layout stays a pair of numbers
 * the Studio and the render already understand.
 */
export function cornerToCardPct(
  corner: Corner,
  cardWidthPct: number = DEFAULT_CARD_WIDTH_PCT,
): { xPct: number; yPct: number } {
  const right = corner === "tr" || corner === "br";
  const bottom = corner === "bl" || corner === "br";
  return {
    // `Math.max(0, …)`: a card measured wider than the frame's usable width
    // would otherwise be placed at a NEGATIVE xPct — off the left edge, to
    // avoid clipping the right. Flush-left is the honest answer there.
    xPct: right ? Math.max(0, 100 - INSET_PCT - cardWidthPct) : INSET_PCT,
    yPct: bottom ? BOTTOM_PCT : TOP_PCT,
  };
}

/**
 * Placement → the corner to highlight. The nearest one: the four anchors form
 * a grid, so nearest-corner decomposes into an independent choice per axis,
 * and a card dragged anywhere in Adjust still lights up a tile when the
 * operator comes back to the step.
 *
 * Takes the SAME width as `cornerToCardPct`, and for the same reason: a wide
 * card's right-hand anchor moves left with it (at 70 % it sits at xPct 24, to
 * the left of the default midpoint), so judging against a fixed anchor would
 * light up the wrong tile for a card the operator can plainly see is on the
 * right.
 */
export function cardPctToCorner(
  p: { xPct: number; yPct: number },
  cardWidthPct: number = DEFAULT_CARD_WIDTH_PCT,
): Corner {
  const rightAnchor = 100 - INSET_PCT - cardWidthPct;
  const right = p.xPct > (INSET_PCT + rightAnchor) / 2;
  const bottom = p.yPct > (TOP_PCT + BOTTOM_PCT) / 2;
  return right ? (bottom ? "br" : "tr") : bottom ? "bl" : "tl";
}

/**
 * The caption patch a SIZE card writes.
 *
 * "Large" carries a second key, and it is not decoration. The Latin default
 * caps the render at two lines (`LATIN_CAPTION_DEFAULTS.maxLines`), which is
 * right at 165 px; at 200 px a real cue (the pipeline caps them near 62
 * characters) does not fit in two lines at ANY wrap, so the renderer drew two
 * lines and let the rest fall off the frame — the operator picked the card
 * that says "Easiest to read on a phone" and watched a word leave the video
 * (QA 2026-09-16, B1). So Large ALSO hands the line count back to the
 * renderer (`maxLines: null` — the UNSET signal, the panel's "Let us decide"),
 * and the wrap becomes whatever the sentence needs. Small and Medium fit
 * inside the cap, so they write the size and nothing else: a cap the operator
 * (or a studied reel) set on purpose is not ours to drop.
 *
 * The step's fit line measures the SAME sentence uncapped, so the number under
 * the cards and the picture beside them now agree.
 */
export function sizePatchFor(
  direction: Direction,
  size: SizeName,
): NonNullable<EditPatch["layout"]>["caption"] {
  const fontSizePx = SIZE_PRESETS[direction][size];
  return size === "large" ? { fontSizePx, maxLines: null } : { fontSizePx };
}

/** The two answers the caption-position step offers. */
export type PositionChoice = "middle" | "lower";

/**
 * The caption patch a position card writes.
 *
 * `bottomPx` is NOT derived in both directions, and that asymmetry is the
 * whole point: the founding RTL caption sits at 240 px with a 115 px face, so
 * `lowerThirdBottomPx(115)` (= 170) would move an Arabic brand's captions 70 px
 * the moment the operator clicked the card that was ALREADY selected. RTL
 * therefore pins the classic distance (`LAYOUT_DEFAULTS.capBottomPx`), while
 * LTR — whose Lower third is a new look with no history to preserve — scales it
 * with the size so the gap stays proportional at Small and Large.
 *
 * The step dispatches this on CLICK only, never on mount, so an untouched
 * walk-through still writes no key at all (spec §2.3).
 */
export function positionPatchFor(
  direction: Direction,
  choice: PositionChoice,
  fontSizePx: number,
): NonNullable<EditPatch["layout"]>["caption"] {
  if (choice === "middle") return { justify: "center", bottomPx: 0 };
  return {
    justify: "flex-end",
    bottomPx: direction === "rtl" ? LAYOUT_DEFAULTS.capBottomPx : lowerThirdBottomPx(fontSizePx),
  };
}

/**
 * Where the logo has to move to when a Lower-third caption would otherwise
 * land on top of it — or `null` when it does not have to move at all.
 *
 * The founding logo sits `LAYOUT_DEFAULTS.logoBottomPx` (620) off the bottom,
 * which was tuned against a caption pinned at 240 px with a 115 px face. A
 * Latin brand on Large + Lower third puts two 200 px lines starting at 290 px,
 * and the second one runs straight through the wordmark — the operator's own
 * two choices, colliding, with nothing on either screen to warn them.
 *
 * So the logo is lifted to clear the caption block: the caption's own bottom,
 * plus two lines at the caption's size (2.3 ×, line height included), plus a
 * 40 px breath, rounded to ten because the number is also a readout in Adjust.
 * Returning `null` below the founding height is what keeps an untouched walk
 * byte-identical — the logo is only ever moved UP, and only when it must be.
 */
export function logoBottomForLowerThird(fontSizePx: number, captionBottomPx: number): number | null {
  const needed = Math.round((captionBottomPx + 2.3 * fontSizePx + 40) / 10) * 10;
  return needed > LAYOUT_DEFAULTS.logoBottomPx ? needed : null;
}

/**
 * What a caption card must say about the LOGO — the patch to fold into its own
 * click, or `null` for "leave the logo alone".
 *
 * Three answers, and the third is the one that was missing: lift it clear of a
 * lower-third caption that would run through it, leave it where it is, or
 * BRING IT BACK to the founding line once the caption no longer needs the room.
 * Without that last one, one click on Large + Lower third left the logo parked
 * mid-frame for the rest of the walk — the operator's later Middle or Small
 * click half-undone, with nothing on screen to explain it.
 *
 * A logo the operator has moved BELOW the founding line is theirs and is never
 * pulled up or down; only a lift this function itself could have made is
 * reversed. Click-only, like every other step write, so an untouched walk
 * still writes no logo key at all.
 */
export function logoPatchForCaption(args: {
  lowered: boolean;
  fontSizePx: number;
  captionBottomPx: number;
  /** What the logo's distance is RIGHT NOW (base ⊕ everything decided). */
  currentLogoBottomPx: number;
}): { bottomPx: number } | null {
  const needed = args.lowered ? logoBottomForLowerThird(args.fontSizePx, args.captionBottomPx) : null;
  if (needed !== null) return needed === args.currentLogoBottomPx ? null : { bottomPx: needed };
  return args.currentLogoBottomPx > LAYOUT_DEFAULTS.logoBottomPx
    ? { bottomPx: LAYOUT_DEFAULTS.logoBottomPx }
    : null;
}

/**
 * The card a measured size belongs on. Ties go to the SMALLER card: a caption
 * exactly between two sizes is safer one notch down (it fits), and the step
 * says "From your reel, sized to fit" when the study's own number moved.
 */
export function nearestSizePreset(direction: Direction, px: number): SizeName {
  const presets = SIZE_PRESETS[direction];
  let best: SizeName = "small";
  let bestGap = Math.abs(px - presets.small);
  for (const name of SIZE_NAMES) {
    const gap = Math.abs(px - presets[name]);
    if (gap < bestGap) {
      best = name;
      bestGap = gap;
    }
  }
  return best;
}
